import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion"; // Import AnimatePresence
import { ArrowUpRight } from "lucide-react";
import "./meet-our-counselors.css";
import LazyBackground from "../../Lazy/lazy-background";
import LoadingDashboard from "../../loading/LoadingDashboard";

const MeetOurCounselors = () => {
  const navigate = useNavigate();
  const [counselors, setCounselors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false); // New state for mobile detection

  const COUNSELORS_API_ENDPOINT = "/wp-json/dbsb/v1/counselors";

  // Function to check if it's a mobile view
  const checkIsMobile = () => {
    setIsMobile(window.innerWidth <= 768); // Adjust breakpoint as needed
  };

  // Effect to set up and clean up the resize listener
  useEffect(() => {
    checkIsMobile(); // Check on initial mount
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Fetch counselors data
  useEffect(() => {
    const fetchCounselors = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${COUNSELORS_API_ENDPOINT}?per_page=10&page=1`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch counselors.");
        }

        const data = await response.json();
        const mappedCounselors = data.counselors.map((counselor) => ({
          id: counselor.slug || counselor.id.toString(),
          name: counselor.name,
          role: counselor.title || "Genetic Counselor",
          image: counselor.featured_image?.url || counselor.avatar_url || `${process.env.PUBLIC_URL}/assets/counselor-svg.svg`,
          bio: counselor.biography || "",
          specialization: counselor.specialization || "",
          experience_years: counselor.experience_years || "",
          email: counselor.email || "",
          phone: counselor.phone || "",
        }));

        setCounselors(mappedCounselors);
      } catch (error) {
        console.error("Error fetching counselors:", error);
        setError(error.message);
        setCounselors([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCounselors();
  }, []);

  // Animation variants for the section container
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren", // Animate parent first, then children
        staggerChildren: 0.1, // Stagger delays for children
        delayChildren: 0.2,   // Delay before children start animating
      },
    },
  };

  // Animation variants for individual counselor cards
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        duration: 0.5,
      },
    },
  };

  // Variants for the slide-up "Read bio" overlay
  const bioOverlayVariants = {
    rest: { y: "100%", transition: { type: "tween", ease: "easeOut", duration: 0.3 } },
    hover: { y: "0%", transition: { type: "tween", ease: "easeIn", duration: 0.2 } },
    // Always visible for mobile
    mobile: { y: "0%", transition: { type: "tween", ease: "easeOut", duration: 0.3 } },
  };

  if (isLoading) {
    return (
      <motion.section
        className="moc-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }} // Add exit animation for when component unmounts
        transition={{ duration: 0.5 }}
      >
        <LazyBackground
          src={`${process.env.PUBLIC_URL}/assets/background12.svg`}
          alt=""
          className="home-background home-background4"
          loading="lazy"
        />
        <LazyBackground
          src={`${process.env.PUBLIC_URL}/assets/background11.svg`}
          alt=""
          className="home-background home-background8"
          loading="lazy"
        />
        <div className="moc-container">
          <motion.div
            className="moc-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="moc-header-title-container">
              <h2>Meet Some of Our Genetic Counselors</h2>
            </div>
            <p>
              Here are a few members of our team who are helping Nigerians make
              empowered health decisions:
            </p>
          </motion.div>
          <div className="loading-container">
            <LoadingDashboard />
          </div>
        </div>
      </motion.section>
    );
  }

  if (counselors.length === 0) {
    return (
      <motion.section
        className="moc-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <LazyBackground
          src={`${process.env.PUBLIC_URL}/assets/background12.svg`}
          alt=""
          className="home-background home-background4"
          loading="lazy"
        />
        <LazyBackground
          src={`${process.env.PUBLIC_URL}/assets/background11.svg`}
          alt=""
          className="home-background home-background8"
          loading="lazy"
        />
        <div className="moc-container">
          <motion.div
            className="moc-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="moc-header-title-container">
              <h2>Meet Some of Our Genetic Counselors</h2>
            </div>
            <p>
              Here are a few members of our team who are helping Nigerians make
              empowered health decisions:
            </p>
          </motion.div>
          <div className="no-counselors">
            <p>No counselors available at the moment.</p>
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <AnimatePresence mode="wait"> {/* Use AnimatePresence for exit animations */}
      <motion.section
        className="moc-section"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible" // Use whileInView to trigger animation when it enters viewport
        viewport={{ once: true, amount: 0.2 }} // Animate only once when 20% visible
        exit="hidden" // Exit animation
      >
        <LazyBackground
          src={`${process.env.PUBLIC_URL}/assets/background12.svg`}
          alt=""
          className="home-background home-background4"
          loading="lazy"
        />
        <LazyBackground
          src={`${process.env.PUBLIC_URL}/assets/background11.svg`}
          alt=""
          className="home-background home-background8"
          loading="lazy"
        />
        <div className="moc-container">
          <motion.div
            className="moc-header"
            initial={{ opacity: 0, y: -20 }} // Initial state for header
            whileInView={{ opacity: 1, y: 0 }} // Animate when in view
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="moc-header-title-container">
              <h2>Meet Some of Our Genetic Counselors</h2>
            </div>
            <p>
              Here are a few members of our team who are helping Nigerians make
              empowered health decisions:
            </p>
          </motion.div>

          <motion.div className="moc-grid">
            {counselors.map((counselor) => (
              <motion.div
                key={counselor.id}
                className="moc-counselor-card"
                variants={cardVariants} // Apply card entrance animation
              >
                <motion.div
                  whileHover={!isMobile ? "hover" : ""} // Only apply hover on desktop
                  initial={isMobile ? "mobile" : "rest"} // Set initial state based on mobile
                  animate={isMobile ? "mobile" : "rest"} // Keep animate to rest for desktop, mobile for mobile
                  className="moc-image-container"
                >
                  <img
                    src={counselor.image}
                    alt={`Portrait of ${counselor.name}`}
                    className="moc-counselor-image"
                    onError={(e) => {
                      e.target.src = `${process.env.PUBLIC_URL}/assets/counselor-svg.svg`;
                    }}
                  />
                  <motion.div
                    className="moc-read-bio-overlay"
                    variants={bioOverlayVariants}
                    onClick={() => navigate(`/counselor-details/${counselor.id}`)}
                  >
                    <span>Read bio</span>
                    <ArrowUpRight size={20} />
                  </motion.div>
                </motion.div>
                <motion.div
                  className="moc-counselor-info"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, amount: 0.8 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  <h3 className="moc-counselor-name">{counselor.name}</h3>
                  <p className="moc-counselor-role">{counselor.role}</p>
                </motion.div>
              </motion.div>
            ))}
            <div className="moc-decorative-line"></div>
          </motion.div>
        </div>
      </motion.section>
    </AnimatePresence>
  );
};

export default MeetOurCounselors;